import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Heart, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

interface MedicalAdvocacyRecord {
  id: number;
  person_id: number;
  agency_id: number;
  date: string | null;
  hospital_id: number | null;
  details: string | null;
  staff_id: number | null;
  created_at: string;
  hospital_name?: string;
  staff_first_name?: string;
  staff_last_name?: string;
}

interface Hospital {
  id: number;
  name: string;
}

interface MedicalAdvocacyListProps {
  personId: string;
}

export function MedicalAdvocacyList({ personId }: MedicalAdvocacyListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MedicalAdvocacyRecord | null>(null);

  const { data, isLoading } = useQuery<{ data: MedicalAdvocacyRecord[] }>({
    queryKey: ["medicalAdvocacy", personId],
    queryFn: () => api.get<{ data: MedicalAdvocacyRecord[] }>(`/clients/${personId}/medical-advocacy`),
    enabled: !!personId,
  });

  const { data: hospitalsData } = useQuery<{ data: Hospital[] }>({
    queryKey: ["hospitals"],
    queryFn: () => api.get<{ data: Hospital[] }>("/admin/hospitals"),
  });

  const createMutation = useMutation({
    mutationFn: (body: { date?: string; hospitalId?: number; details?: Record<string, unknown>; staffId?: number }) =>
      api.post(`/clients/${personId}/medical-advocacy`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicalAdvocacy", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; date?: string; hospitalId?: number; details?: Record<string, unknown>; staffId?: number }) =>
      api.put(`/clients/${personId}/medical-advocacy/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicalAdvocacy", personId] });
      setEditingRecord(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/medical-advocacy/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["medicalAdvocacy", personId] }),
  });

  const records = data?.data ?? [];
  const hospitals = hospitalsData?.data ?? [];

  function parseDetails(raw: string | null): Record<string, unknown> | null {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Medical Advocacy</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Record
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <MedicalForm
              hospitals={hospitals}
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingRecord && (
        <Card>
          <CardContent className="p-4">
            <MedicalForm
              hospitals={hospitals}
              initial={editingRecord}
              onSubmit={(d) => updateMutation.mutate({ id: editingRecord.id, ...d })}
              onCancel={() => setEditingRecord(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted" />)}
        </div>
      )}

      {!isLoading && records.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Heart className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No medical advocacy records.</p>
          </CardContent>
        </Card>
      )}

      {records.map((r) => {
        const staffName = r.staff_first_name && r.staff_last_name
          ? `${r.staff_first_name} ${r.staff_last_name}` : null;
        const details = parseDetails(r.details);

        return (
          <Card key={r.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium">
                    {r.hospital_name ?? "Medical Advocacy"} #{r.id}
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    {r.date ? new Date(r.date).toLocaleDateString() : new Date(r.created_at).toLocaleDateString()}
                    {staffName && ` | Staff: ${staffName}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditingRecord(r)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => {
                    if (confirm("Delete this medical advocacy record?")) deleteMutation.mutate(r.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {details && (
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(details).map(([key, value]) => (
                    <div key={key} className="contents">
                      <dt className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</dt>
                      <dd className="font-medium">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

interface MedicalFormProps {
  hospitals: Hospital[];
  initial?: MedicalAdvocacyRecord;
  onSubmit: (data: { date?: string; hospitalId?: number; details?: Record<string, unknown>; staffId?: number }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function MedicalForm({ hospitals, initial, onSubmit, onCancel, isLoading }: MedicalFormProps) {
  const [date, setDate] = useState(initial?.date ?? "");
  const [hospitalId, setHospitalId] = useState(initial?.hospital_id ? String(initial.hospital_id) : "");
  const [detailsJson, setDetailsJson] = useState(() => {
    if (!initial?.details) return "";
    try { return JSON.stringify(JSON.parse(initial.details), null, 2); } catch { return initial.details; }
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Hospital</label>
          <select
            value={hospitalId}
            onChange={(e) => setHospitalId(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select hospital...</option>
            {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Details (JSON)</label>
        <textarea
          value={detailsJson}
          onChange={(e) => setDetailsJson(e.target.value)}
          rows={4}
          placeholder='{"department": "ER", "disposition": "Treated and released", "referralType": "SANE exam"}'
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => {
            let details: Record<string, unknown> | undefined;
            if (detailsJson) {
              try { details = JSON.parse(detailsJson); } catch { details = { raw: detailsJson }; }
            }
            onSubmit({
              date: date || undefined,
              hospitalId: hospitalId ? parseInt(hospitalId, 10) : undefined,
              details,
            });
          }}
          loading={isLoading}
        >
          {initial ? "Update Record" : "Add Record"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
